// Regexes used to detect which addon plays which structural role when
// building a sign's parts automatically (Face Plate/Backer/Window/Accent)
// - matched against cost_label, the same addon-catalog "category" proxy
// already used elsewhere in this file. Hardcoded for now rather than an
// admin-editable table (see DRAWING_DATA.md's "hardcode simple/known
// cases first" decision) - confirmed against a previously-working
// prototype (./proof) that solved this same problem with plain substring
// matching for the same roles; these are regex instead so common real-
// world label variations (spacing, "Shim Plate" as a Backer synonym)
// are tolerated without restructuring the matching code later.
const FACE_PLATE_COST_LABEL_PATTERN = /face\s*plate/i
const BACKER_COST_LABEL_PATTERN = /backer|shim\s*plate/i
const WINDOW_COST_LABEL_PATTERN = /window/i
const ACCENT_LABEL_PATTERN = /accent/i

// Strips a leading dimension prefix (e.g. '1/4" ') off an addon label to
// get a clean part description, e.g. '1/4" Painted Backer' -> 'Painted
// Backer' - same approach as ./proof.
const DIMENSION_PREFIX_PATTERN = /^[\d/]+"?\s*/

// A part's material, guessed from whichever of these common sign
// materials appears in its addon's label - same keyword list as
// ./proof's own material-guessing helper.
const MATERIAL_KEYWORDS = [
    'Aluminum', 'Acrylic', 'Laminate', 'Sintra', 'Dibond', 'Foam', 'Vinyl',
]

// A fraction-inch (1/4") or decimal-inch (0.25") thickness value anywhere
// in an addon's label - broader than autoFillThickness's own pattern
// (fractions only) since here we're reading a single already-identified
// addon's label directly, not searching broadly across a cost-label group.
const THICKNESS_VALUE_PATTERN = /(\d+\/\d+|\d+\.\d+)"/

/**
 * One row of a sign's Drawing Data part table
 */
class Part {
    /**
     * @param {string} partNumber
     * @param {string} description
     * @param {string} material
     * @param {string} size
     * @param {string} thickness
     * @param {string} paint
     * @param {string} backgroundColor
     * @param {string} textileColors
     * @param {string} corners
     * @param {string} edges
     * @param {string} finish
     */
    constructor(
        partNumber='', description='', material='', size='', thickness='',
        paint='', backgroundColor='', textileColors='', corners='', edges='',
        finish='',
    ) {
        this.part_number = partNumber
        this.description = description
        this.material = material
        this.size = size
        this.thickness = thickness
        this.paint = paint
        this.background_color = backgroundColor
        this.textile_colors = textileColors
        this.corners = corners
        this.edges = edges
        this.finish = finish
    }

    /**
     * @param {json} json
     * @return {Part}
     */
    static from(json) {
        return Object.assign(new Part(), json)
    }
}

/**
 * A sign's manually-maintained production spec (font, mounting, and its
 * part rows) - see DRAWING_DATA.md for the full design.
 */
class DrawingData {
    /**
     * @param {string} signId
     * @param {string} font
     * @param {string} mounting
     * @param {[Part]} partData
     * @param {number} dimensionScale a whole-number percentage (e.g. 100
     *      for 100%), manually entered by the end user for the
     *      dimensioned drawing image - no auto-fill source, unlike
     *      font/mounting
     * @param {number} overviewScale a whole-number percentage, for the
     *      overview/variants image
     */
    constructor(
        signId='', font='', mounting='', partData=[],
        dimensionScale=100, overviewScale=100,
    ) {
        this.sign_id = signId
        this.font = font
        this.mounting = mounting
        this.part_data = partData
        this.dimension_scale = dimensionScale
        this.overview_scale = overviewScale
    }

    /**
     * @param {json} json
     * @return {DrawingData}
     */
    static from(json) {
        const drawingData = Object.assign(new DrawingData(), json)
        drawingData.part_data = drawingData.part_data.map(Part.from)
        // Older saved rows predate the scale columns, so the DB sends
        // back null rather than a real value - default those the same
        // way a brand-new sign's first view does (autoFillFor). Uses ??
        // rather than || since 0 is a legitimate (if unusual) scale value.
        drawingData.dimension_scale = drawingData.dimension_scale ?? 100
        drawingData.overview_scale = drawingData.overview_scale ?? 100
        return drawingData
    }

    /**
     * The letter assigned to a part row by its position - 0 -> "A",
     * 1 -> "B", ... 25 -> "Z", 26 -> "AA", matching spreadsheet columns.
     * @param {number} index
     * @return {string}
     */
    static partNumberForIndex(index) {
        let n = index
        let letters = ''
        do {
            letters = String.fromCharCode(65 + (n % 26)) + letters
            n = Math.floor(n / 26) - 1
        } while (n >= 0)
        return letters
    }

    /**
     * Reads a colour location out of a metadata "colors" attribute entry.
     * Real extraction output has taken two shapes: a JSON-encoded object
     * string (e.g. '{"color": "white", "location": "surface"}'), or a
     * plain color-name string with no location info at all (e.g.
     * "Sherwin Williams SW 7068 Grizzle Gray"). A plain string is returned
     * as {color: entry, location: null} - callers treat a null location
     * as "could apply anywhere" rather than excluding it.
     * @param {*} entry one item from a metadata "colors" array
     * @return {?{color: string, location: ?string}}
     */
    static parseColorEntry(entry) {
        if (entry && typeof entry === 'object') {
            return entry
        }
        if (typeof entry === 'string') {
            try {
                const parsed = JSON.parse(entry)
                if (parsed && typeof parsed === 'object') {
                    return parsed
                }
            } catch (e) {
                // Not JSON - a plain color string, handled below.
            }
            return {color: entry, location: null}
        }
        return null
    }

    /**
     * Every color entry from a metadata "colors" attribute value whose
     * location matches (case-insensitive), parsed defensively. A color
     * with no location info (a plain string entry) matches every location,
     * since there's nothing to say it doesn't belong here.
     * @param {?[*]} colors a metadata "colors" attribute value
     * @param {string} location e.g. "background"
     * @return {[string]} matching colors, in order
     */
    static colorsForLocation(colors, location) {
        if (!Array.isArray(colors)) {
            return []
        }
        const lowerLocation = location.toLowerCase()
        const matchesLocation = (entry) =>
            entry.color && (!entry.location || entry.location.toLowerCase() === lowerLocation)
        return colors
            .map(DrawingData.parseColorEntry)
            .filter(Boolean)
            .filter(matchesLocation)
            .map((entry) => entry.color)
    }

    /**
     * "Give me each of the colours in a json and where they go" metadata
     * output, grouped by location - e.g. "Surface: white, Subsurface: yellow"
     * - excludes "background", which gets its own dedicated column/field.
     * A color with no location info (a plain string entry) is listed as-is
     * rather than grouped, since there's no location to group it under.
     * @param {?[*]} colors a metadata "colors" attribute value
     * @return {string}
     */
    static formatTextileColors(colors) {
        if (!Array.isArray(colors) || colors.length === 0) {
            return ''
        }
        const byLocation = {}
        const unlocated = []
        for (const raw of colors) {
            const entry = DrawingData.parseColorEntry(raw)
            if (!entry || !entry.color) {
                continue
            }
            if (!entry.location) {
                unlocated.push(entry.color)
                continue
            }
            if (entry.location.toLowerCase() === 'background') {
                continue
            }
            if (!byLocation[entry.location]) {
                byLocation[entry.location] = []
            }
            byLocation[entry.location].push(entry.color)
        }
        return [
            ...Object.entries(byLocation).map(([location, cs]) => `${location}: ${cs.join(', ')}`),
            ...unlocated,
        ].join(', ')
    }

    /**
     * The shared shape behind Material/Thickness/Edges/Paint: check a
     * Metadata attribute first, else search the sign's own addons and
     * resolve to one or more of their labels. cost_label is used as the
     * best-available proxy for the addon catalog's "category" grouping
     * (header_label), since that field isn't copied onto a sign's picked
     * addons when chosen - not yet confirmed against real addon data, see
     * DRAWING_DATA.md.
     * @param {json} metadataAttributes
     * @param {Sign} sign
     * @param {object} options
     * @param {?string} options.metadataKey checked first, used as-is if present
     * @param {?[string]} options.costLabels only addons whose cost_label
     *      (case-insensitive) is one of these match; omit to match by
     *      options.labelContains instead
     * @param {?string} options.labelContains only addons whose label or
     *      cost_label (case-insensitive) contains this match
     * @param {?RegExp} options.pattern if set, extract this pattern's
     *      first match from a matching addon's label instead of the whole label
     * @param {boolean} options.joinAll true to comma-join every matching
     *      addon's resolved value, false (default) to use just the first
     * @param {string} options.fallback used when nothing matches (default '')
     * @return {string}
     */
    static autoFillFromMetadataOrAddons(metadataAttributes, sign, options) {
        if (options.metadataKey && metadataAttributes?.[options.metadataKey]) {
            return metadataAttributes[options.metadataKey]
        }

        const lowerCostLabels = options.costLabels?.map((c) => c.toLowerCase())
        const lowerContains = options.labelContains?.toLowerCase()
        const matchesAddon = (addon) => {
            const costLabel = (addon.cost_label ?? '').toLowerCase()
            const label = (addon.label ?? '').toLowerCase()
            if (lowerCostLabels) {
                return lowerCostLabels.includes(costLabel)
            }
            if (lowerContains) {
                return label.includes(lowerContains) ||
                    costLabel.includes(lowerContains)
            }
            return false
        }

        const resolved = (sign.addons ?? [])
            .filter(matchesAddon)
            .map((addon) => {
                if (!options.pattern) {
                    return addon.label
                }
                const match = (addon.label ?? '').match(options.pattern)
                return match ? match[0] : null
            })
            .filter(Boolean)

        if (resolved.length === 0) {
            return options.fallback ?? ''
        }
        return options.joinAll ? resolved.join(', ') : resolved[0]
    }

    /**
     * Metadata's own "sign_description" attribute first, else the sign's
     * given_id, e.g. "SV".
     * @param {json} metadataAttributes
     * @param {Sign} sign
     * @return {string}
     */
    static autoFillDescription(metadataAttributes, sign) {
        return metadataAttributes?.sign_description || sign.given_id || ''
    }

    /**
     * Metadata's own "size" attribute first (already nicely formatted by
     * extraction, e.g. 4 2/3" x 5 1/8"), else a plain width x height.
     * @param {json} metadataAttributes
     * @param {Sign} sign
     * @return {string}
     */
    static autoFillSize(metadataAttributes, sign) {
        if (metadataAttributes?.size) {
            return metadataAttributes.size
        }
        if (sign.width && sign.height) {
            return `${sign.width}" x ${sign.height}"`
        }
        return ''
    }

    /**
     * A part's own material - scoped by part position the same way
     * autoFillThickness already is, so a Face part's material doesn't
     * also pull in a Backer addon's material now that Backer gets its
     * own dedicated part (see autoFillParts).
     * @param {json} metadataAttributes
     * @param {Sign} sign
     * @param {number} partIndex
     * @return {string}
     */
    static autoFillMaterial(metadataAttributes, sign, partIndex) {
        return DrawingData.autoFillFromMetadataOrAddons(
            metadataAttributes, sign, {
                metadataKey: 'material',
                costLabels: partIndex === 0 ?
                    ['Face Plate', 'Aluminum', 'Material'] : ['Backer'],
                joinAll: true,
            })
    }

    /**
     * A part's own thickness - Metadata first, else a fraction-inch pattern
     * (e.g. matching '1/8" Acrylic') searched only within the addon group
     * relevant to this part: the sign face (index 0) looks at "Face Plate"
     * addons, any later part looks at "Backer" addons. This part-position
     * heuristic is a guess, not confirmed - see DRAWING_DATA.md.
     * @param {json} metadataAttributes
     * @param {Sign} sign
     * @param {number} partIndex
     * @return {string}
     */
    static autoFillThickness(metadataAttributes, sign, partIndex) {
        return DrawingData.autoFillFromMetadataOrAddons(
            metadataAttributes, sign, {
                metadataKey: 'thickness',
                costLabels: [partIndex === 0 ? 'Face Plate' : 'Backer'],
                pattern: /[1-9][0-9]*\s*\/\s*[1-9][0-9]*\s*"/,
            })
    }

    /**
     * Dropdown value - Surface/Subsurface/N/A/Other - pre-selected by
     * scanning the sign's addon labels for one mentioning "paint", checking
     * "subsurface" before "surface" since the former contains the latter.
     * @param {Sign} sign
     * @return {string}
     */
    static autoFillPaint(sign) {
        const paintLabel = DrawingData.autoFillFromMetadataOrAddons(
            null, sign, {labelContains: 'paint'})
        if (!paintLabel) {
            return ''
        }
        const lower = paintLabel.toLowerCase()
        if (lower.includes('subsurface')) {
            return 'Subsurface'
        }
        if (lower.includes('surface')) {
            return 'Surface'
        }
        return ''
    }

    /**
     * @param {json} metadataAttributes
     * @return {string}
     */
    static autoFillTextileColors(metadataAttributes) {
        return DrawingData.formatTextileColors(metadataAttributes?.colors)
    }

    /**
     * Metadata colors whose location is "background", comma-joined -
     * pulled out as its own field rather than folded into Textile Colors.
     * @param {json} metadataAttributes
     * @return {string}
     */
    static autoFillBackgroundColor(metadataAttributes) {
        return DrawingData
            .colorsForLocation(metadataAttributes?.colors, 'background')
            .join(', ')
    }

    /**
     * @param {json} metadataAttributes
     * @return {string}
     */
    static autoFillCorners(metadataAttributes) {
        return metadataAttributes?.corners ?? ''
    }

    /**
     * Every named edge treatment mentioned across a sign's addon labels
     * (eased/beveled/bullnosed - recognized by name, not by the word
     * "edge" appearing), comma-joined if more than one is found. Mirrors
     * a previously-working prototype's own edge-detection (./proof),
     * which is why these specific three names are the ones recognized.
     * @param {Sign} sign
     * @return {[string]}
     */
    static detectEdgeTreatments(sign) {
        const treatments = []
        for (const addon of sign.addons ?? []) {
            const label = (addon.pdf_label || addon.label || '').toLowerCase()
            if (label.includes('eased') && !treatments.includes('Eased')) {
                treatments.push('Eased')
            }
            if (label.includes('bevel') && !treatments.includes('Beveled')) {
                treatments.push('Beveled')
            }
            if (label.includes('bullnose') &&
                !treatments.includes('Bullnosed')) {
                treatments.push('Bullnosed')
            }
        }
        return treatments
    }

    /**
     * Metadata's "edge" attribute first, else any named edge treatment
     * found on the sign's addons (see detectEdgeTreatments), else any
     * addon just generically mentioning the word "edge", else "Square" -
     * a flat/square-cut edge is the default treatment absent any other
     * addon indicating otherwise, confirmed against a previously-working
     * prototype (see ./proof) that used the same real-world default.
     * @param {json} metadataAttributes
     * @param {Sign} sign
     * @return {string}
     */
    static autoFillEdges(metadataAttributes, sign) {
        if (metadataAttributes?.edge) {
            return metadataAttributes.edge
        }
        const treatments = DrawingData.detectEdgeTreatments(sign)
        if (treatments.length > 0) {
            return treatments.join(', ')
        }
        return DrawingData.autoFillFromMetadataOrAddons(
            metadataAttributes, sign, {
                labelContains: 'edge',
                fallback: 'Square',
            })
    }

    /**
     * @param {json} metadataAttributes
     * @return {string}
     */
    static autoFillFinish(metadataAttributes) {
        return metadataAttributes?.finish ?? ''
    }

    /**
     * @param {json} metadataAttributes
     * @return {string}
     */
    static autoFillFont(metadataAttributes) {
        return metadataAttributes?.font ?? ''
    }

    /**
     * @param {json} metadataAttributes
     * @return {string}
     */
    static autoFillMounting(metadataAttributes) {
        return metadataAttributes?.mounting ?? ''
    }

    /**
     * Builds one auto-filled Part for a brand-new row at the given index -
     * part_number is assigned from the index itself (positional, not
     * sourced from quote/metadata data).
     * @param {number} index
     * @param {json} metadataAttributes this sign's Metadata attributes
     * @param {Sign} sign
     * @return {Part}
     */
    static autoFillPart(index, metadataAttributes, sign) {
        return new Part(
            DrawingData.partNumberForIndex(index),
            DrawingData.autoFillDescription(metadataAttributes, sign),
            DrawingData.autoFillMaterial(metadataAttributes, sign, index),
            DrawingData.autoFillSize(metadataAttributes, sign),
            DrawingData.autoFillThickness(metadataAttributes, sign, index),
            DrawingData.autoFillPaint(sign),
            DrawingData.autoFillBackgroundColor(metadataAttributes),
            DrawingData.autoFillTextileColors(metadataAttributes),
            DrawingData.autoFillCorners(metadataAttributes),
            DrawingData.autoFillEdges(metadataAttributes, sign),
            DrawingData.autoFillFinish(metadataAttributes),
        )
    }

    /**
     * The first addon on the sign whose given field matches pattern.
     * @param {Sign} sign
     * @param {RegExp} pattern
     * @param {string} [field] 'cost_label' (default) or 'label'
     * @return {?SignAddon}
     */
    static findAddonByPattern(sign, pattern, field='cost_label') {
        return (sign.addons ?? []).find(
            (addon) => pattern.test(addon[field] ?? ''))
    }

    /**
     * A clean part description from an addon's own label, stripping a
     * leading dimension prefix (e.g. '1/4" Painted Backer' -> 'Painted
     * Backer') - same approach as ./proof. Falls back to a generic
     * description (e.g. 'Backer/Shim Plate') if nothing's left after
     * stripping.
     * @param {SignAddon} addon
     * @param {string} fallback
     * @return {string}
     */
    static describeAddon(addon, fallback) {
        const label = addon.pdf_label || addon.label || ''
        const cleaned = label.replace(DIMENSION_PREFIX_PATTERN, '').trim()
        return cleaned || fallback
    }

    /**
     * @param {SignAddon} addon
     * @return {string} e.g. 'Acrylic', or the whole label if no known
     *      material keyword is found
     */
    static extractMaterialFromAddon(addon) {
        const label = addon.pdf_label || addon.label || ''
        const lowerLabel = label.toLowerCase()
        const keyword = MATERIAL_KEYWORDS.find(
            (m) => lowerLabel.includes(m.toLowerCase()))
        return keyword ?? label
    }

    /**
     * @param {SignAddon} addon
     * @return {string} e.g. '1/4"', or '' if no thickness value is found
     */
    static extractThicknessFromAddon(addon) {
        const label = addon.pdf_label || addon.label || ''
        const match = label.match(THICKNESS_VALUE_PATTERN)
        return match ? match[0] : ''
    }

    /**
     * Builds one Part for a structural addon detected on the sign (a
     * Backer/Shim Plate, Window, or Accent Bar) - same size as the sign
     * face (these are physically the same footprint) and same
     * Corners/Finish as every other part of this sign (metadata-driven,
     * same as the sign face - not hardcoded), material/thickness read
     * directly off that addon's own label. Edges is passed in explicitly
     * rather than derived here, since the right source differs by caller
     * (the main sign's own detected/metadata edges vs. a subsign's own).
     * @param {SignAddon} addon
     * @param {number} index
     * @param {string} fallbackDescription
     * @param {string} faceSize
     * @param {string} edges
     * @param {json} metadataAttributes
     * @return {Part}
     */
    static autoFillAddonPart(
        addon, index, fallbackDescription, faceSize, edges, metadataAttributes,
    ) {
        return new Part(
            DrawingData.partNumberForIndex(index),
            DrawingData.describeAddon(addon, fallbackDescription),
            DrawingData.extractMaterialFromAddon(addon),
            faceSize,
            DrawingData.extractThicknessFromAddon(addon),
            '',
            '',
            '',
            DrawingData.autoFillCorners(metadataAttributes),
            edges,
            DrawingData.autoFillFinish(metadataAttributes),
        )
    }

    /**
     * Builds the parts contributed by one subsign - its own face (with
     * its own edge-treatment detection, scoped to the subsign's own
     * addons rather than the main sign's) plus a Backer/Shim Plate part
     * if the subsign has its own backer addon.
     * @param {Sign} subsign
     * @param {number} startIndex
     * @param {json} metadataAttributes
     * @return {[Part]}
     */
    static autoFillSubsignParts(subsign, startIndex, metadataAttributes) {
        const size = subsign.width && subsign.height ?
            `${subsign.width}" x ${subsign.height}"` : ''
        const treatments = DrawingData.detectEdgeTreatments(subsign)
        const edges = treatments.length > 0 ? treatments.join(', ') : 'Square'
        const faceAddon = DrawingData.findAddonByPattern(
            subsign, FACE_PLATE_COST_LABEL_PATTERN)
        const parts = [new Part(
            DrawingData.partNumberForIndex(startIndex),
            subsign.given_id ? `Subsign ${subsign.given_id}` : 'Subsign',
            faceAddon ? DrawingData.extractMaterialFromAddon(faceAddon) : '',
            size,
            faceAddon ? DrawingData.extractThicknessFromAddon(faceAddon) : '',
            '',
            '',
            '',
            DrawingData.autoFillCorners(metadataAttributes),
            edges,
            DrawingData.autoFillFinish(metadataAttributes),
        )]
        const backerAddon = DrawingData.findAddonByPattern(
            subsign, BACKER_COST_LABEL_PATTERN)
        if (backerAddon) {
            parts.push(DrawingData.autoFillAddonPart(
                backerAddon, startIndex + 1, 'Backer/Shim Plate', size, edges,
                metadataAttributes))
        }
        return parts
    }

    /**
     * Builds a sign's initial part list for a brand-new, never-saved
     * DrawingData row. If Metadata already specifies distinct parts (more
     * than just the whole-sign row), that explicit structure is trusted
     * as-is - one Part per Metadata row, unchanged from before. Otherwise,
     * parts are derived from which structural addons are actually on the
     * sign: always a Sign Face part, plus a Backer/Shim Plate, Window, or
     * Accent Bar part for each of those addons found (independently - a
     * sign with both a Backer and a Window addon gets both parts, not
     * just one), plus a face (and possible backer) part per subsign.
     * Mirrors a previously-working prototype (./proof) that solved this
     * same "how many parts does this sign actually have" problem the same
     * way. Hardcoded for now, not an admin-editable table - see
     * DRAWING_DATA.md's decision to hardcode simple/known cases first.
     * @param {[json]} metadataAttributesList
     * @param {Sign} sign
     * @return {[Part]}
     */
    static autoFillParts(metadataAttributesList, sign) {
        if (metadataAttributesList.length > 1) {
            return metadataAttributesList.map((attributes, index) =>
                DrawingData.autoFillPart(index, attributes, sign))
        }

        const metadataAttributes = metadataAttributesList[0] ?? {}
        const parts = [DrawingData.autoFillPart(0, metadataAttributes, sign)]
        const faceSize = parts[0].size
        // Reuse the face's own already-computed Edges value (metadata or
        // detected treatment) rather than re-deriving it, so every part
        // of this sign shares one consistent Edges value - same principle
        // as Corners/Finish already being identical across every part.
        const faceEdges = parts[0].edges

        const backerAddon = DrawingData.findAddonByPattern(
            sign, BACKER_COST_LABEL_PATTERN)
        if (backerAddon) {
            parts.push(DrawingData.autoFillAddonPart(
                backerAddon, parts.length, 'Backer/Shim Plate', faceSize,
                faceEdges, metadataAttributes))
        }
        const windowAddon = DrawingData.findAddonByPattern(
            sign, WINDOW_COST_LABEL_PATTERN)
        if (windowAddon) {
            parts.push(DrawingData.autoFillAddonPart(
                windowAddon, parts.length, 'Window', faceSize, faceEdges,
                metadataAttributes))
        }
        const accentAddon = DrawingData.findAddonByPattern(
            sign, ACCENT_LABEL_PATTERN, 'label')
        if (accentAddon) {
            parts.push(DrawingData.autoFillAddonPart(
                accentAddon, parts.length, 'Accent Bar', faceSize, faceEdges,
                metadataAttributes))
        }

        for (const subsign of sign.subsigns ?? []) {
            parts.push(...DrawingData.autoFillSubsignParts(
                subsign, parts.length, metadataAttributes))
        }

        return parts
    }

    /**
     * Builds a fully auto-filled DrawingData for a sign that has no saved
     * row yet - see DRAWING_DATA.md's Flow section. Only ever called once,
     * for a sign's first-ever view, never to re-suggest over saved data.
     * Font/Mounting only ever come from the whole-sign row (index 0).
     * @param {Sign} sign
     * @param {[json]} metadataAttributesList this sign's Metadata
     *      attributes, one entry per Metadata row (whole-sign row first,
     *      if present) - [{}] if the sign has no Metadata entry yet
     * @return {DrawingData}
     */
    static autoFillFor(sign, metadataAttributesList) {
        const list = metadataAttributesList?.length > 0 ? metadataAttributesList : [{}]
        return new DrawingData(
            sign.id,
            DrawingData.autoFillFont(list[0]),
            DrawingData.autoFillMounting(list[0]),
            DrawingData.autoFillParts(list, sign),
            100,
            100,
        )
    }
}

module.exports = {
    DrawingData,
    Part,
}
